import type { CrowdexUIMessage } from '@crowdex/agent-contracts';
import { Button } from '@crowdex/ui/components/button';
import { Checkbox } from '@crowdex/ui/components/checkbox';
import { Input } from '@crowdex/ui/components/input';
import { RadioGroup, RadioGroupItem } from '@crowdex/ui/components/radio-group';
import { cn } from '@crowdex/ui/lib/utils';
import { ChevronRight, Laptop, MessageCircleQuestion, ShieldCheck } from 'lucide-react';
import { type ReactNode, useContext, useRef, useState } from 'react';
import { DockKeysReachProvider, Key, useDockKeys } from '@/components/dock-keys';
import { useWorkbench } from '@/features/plugins/use-plugin-workbench';
import { InterruptedActionContext } from '@/lib/conversations/message-part/interrupted-action-context';
import { stringValue } from '@/lib/conversations/message-part/text';
import {
  type AnswerToolApproval,
  type PendingToolApproval,
  pendingToolApprovals,
  type ToolApprovalAnswer,
} from '@/lib/conversations/message-part/tool-approval';
import { isQuestionToolPart, questionItems } from '@/lib/conversations/message-part/tool-identity';
import type { DynamicToolPart } from '@/lib/conversations/message-part/types';
import { useOptionalDesktopShellStoreApi } from '@/lib/desktop-shell/store-context';
import { showPanelView } from '@/lib/side-panel/panel-tabs';

/** The command an approval runs, when it runs one. */
function approvalCommand(input: unknown): string | null {
  if (typeof input !== 'object' || input === null) return null;
  const values = input as Record<string, unknown>;
  const command = values['command'] ?? values['cmd'];
  if (typeof command === 'string') return command;
  if (Array.isArray(command) && command.every((part) => typeof part === 'string'))
    return command.join(' ');
  return null;
}

/**
 * The tool the agent proposes the handoff with (design D48b). No core tool emits it yet: once one
 * does (`conversation.continue_on_this_computer` of code mode), its approval docks as this.
 */
const HANDOFF_PROPOSAL_TOOL = 'continue_on_this_computer';

function isHandoffProposal(approval: PendingToolApproval): boolean {
  return approval.toolName === HANDOFF_PROPOSAL_TOOL;
}

function handoffReason(input: unknown): string | null {
  if (typeof input !== 'object' || input === null) return null;
  const values = input as Record<string, unknown>;
  const reason = values['reason'] ?? values['why'] ?? values['message'];
  return typeof reason === 'string' && reason.trim() !== '' ? reason.trim() : null;
}

/** The question the agent waits on now (its tool call is open), in the latest message. */
export function pendingQuestion(messages: readonly CrowdexUIMessage[]): DynamicToolPart | null {
  const message = messages.at(-1);
  if (message?.role !== 'assistant') return null;
  const part = message.parts.findLast(
    (candidate): candidate is DynamicToolPart =>
      candidate.type === 'dynamic-tool' &&
      candidate.state === 'input-available' &&
      isQuestionToolPart(candidate),
  );
  return part ?? null;
}

/** The amber frame of what waits for the user, above the composer (design D13–D14). */
export function DockFrame({
  icon,
  label,
  children,
  keys,
  ...props
}: {
  icon: ReactNode;
  label: string;
  children: ReactNode;
  keys: ReturnType<typeof useDockKeys>;
} & Record<`data-${string}`, string | undefined>) {
  return (
    <section
      aria-label={label}
      aria-live="polite"
      className="mx-auto mb-3 max-w-conversation rounded-card border border-attention bg-card px-4 pt-3 pb-3.5 outline-none focus-visible:ring-2 focus-visible:ring-attention/40"
      ref={keys.frameRef}
      tabIndex={-1}
      {...props}
    >
      <p className="type-meta flex items-center gap-1.5 font-medium text-attention max-md:type-section">
        {icon}
        {label}
      </p>
      <DockKeysReachProvider value={keys.reach}>{children}</DockKeysReachProvider>
    </section>
  );
}

function ApprovalDock({
  approval,
  count,
  onAnswer,
}: {
  approval: PendingToolApproval;
  count: number;
  onAnswer: (answer: ToolApprovalAnswer, kind: 'command' | 'handoff' | 'other') => void;
}) {
  const [remember, setRemember] = useState(false);
  const [details, setDetails] = useState(false);
  const store = useOptionalDesktopShellStoreApi();
  const cloud = store?.getState().native === false;
  const command = approvalCommand(approval.input);
  const handoff = isHandoffProposal(approval);
  const kind = handoff ? 'handoff' : command !== null ? 'command' : 'other';
  const approve = () =>
    onAnswer(
      {
        id: approval.approvalId,
        approved: true,
        ...(remember && approval.remember !== undefined ? { remember: true } : {}),
      },
      kind,
    );
  const deny = () => onAnswer({ id: approval.approvalId, approved: false }, kind);
  const keys = useDockKeys({ approve, deny });
  const counter = count > 1 ? ` · 1 of ${count}` : '';

  const buttons = (approveLabel: string, denyLabel: string) => (
    // A long label (Continue on this computer) takes a row of its own rather than overflowing.
    <div className="ml-auto flex items-center gap-2 max-md:w-full max-md:flex-wrap">
      <Button
        className="h-9 rounded-control px-4 max-md:h-12 max-md:flex-1 max-md:text-base"
        onClick={deny}
        variant="outline"
      >
        {denyLabel}
        <Key>Esc</Key>
      </Button>
      <Button
        className="h-9 rounded-control px-4 max-md:h-12 max-md:flex-1 max-md:text-base"
        onClick={approve}
      >
        {approveLabel}
        <Key>⌘↵</Key>
      </Button>
    </div>
  );

  if (handoff) {
    return (
      <DockFrame
        data-dock="handoff"
        keys={keys}
        icon={<Laptop aria-hidden className="size-3.5" />}
        label={`Continue on this computer?${counter}`}
      >
        <p className="type-body mt-2 text-foreground">
          {handoffReason(approval.input) ??
            'The agent asks to move this conversation to a worktree on this computer.'}
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {buttons('Continue on this computer', 'Not now')}
        </div>
      </DockFrame>
    );
  }

  const shown = command ?? (details ? JSON.stringify(approval.input ?? null, null, 2) : null);
  const rememberLabel = approval.remember;
  return (
    <DockFrame
      data-dock="approval"
      keys={keys}
      icon={
        approval.icon === undefined ? (
          <ShieldCheck aria-hidden className="size-3.5" />
        ) : (
          <img alt="" className="size-3.5" src={approval.icon} />
        )
      }
      label={`${command !== null ? 'Run a command?' : approval.title}${counter}`}
    >
      {shown !== null && (
        <pre className="mt-2.5 max-h-40 overflow-auto whitespace-pre-wrap break-all rounded-xl bg-code-surface px-3 py-2.5 font-mono text-foreground text-sm">
          {shown}
        </pre>
      )}
      <p className="type-meta mt-2 text-muted-foreground">
        {command !== null
          ? `${cloud ? 'In the cloud sandbox' : 'On this computer'} · reads and writes the repository`
          : approval.remember === undefined
            ? 'The agent is waiting for your approval.'
            : 'The agent wants to use this app.'}
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-3 max-md:flex-col-reverse max-md:items-stretch">
        {rememberLabel !== undefined && (
          // biome-ignore lint/a11y/noLabelWithoutControl: the Radix checkbox inside is the control.
          <label className="type-meta flex cursor-pointer items-center gap-2 text-foreground/85 max-md:justify-center pointer-coarse:min-h-11">
            <Checkbox
              aria-label={rememberLabel}
              checked={remember}
              onCheckedChange={(value) => setRemember(value === true)}
            />
            <RememberLabel text={rememberLabel} />
          </label>
        )}
        {command === null && approval.input !== undefined && approval.input !== null && (
          <button
            aria-expanded={details}
            className="type-meta flex items-center gap-1 text-muted-foreground hover:text-foreground"
            onClick={() => setDetails((open) => !open)}
            type="button"
          >
            <ChevronRight className={cn('size-3 transition-transform', details && 'rotate-90')} />
            Details
          </button>
        )}
        {buttons('Approve', 'Deny')}
      </div>
    </DockFrame>
  );
}

/** "Always allow `pnpm test` here": the command it covers in mono. */
function RememberLabel({ text }: { text: string }) {
  const match = /^(Always allow\s+)(.+?)(\s+here)?$/i.exec(text);
  if (match === null) return <span>{text}</span>;
  return (
    <span>
      {match[1]}
      <code className="font-mono">{match[2]}</code>
      {match[3] ?? ''}
    </span>
  );
}

type QuestionOption = { label: string; description: string; recommended: boolean };

function questionOptions(question: Record<string, unknown>): QuestionOption[] {
  const options = Array.isArray(question['options']) ? question['options'] : [];
  return options.flatMap((option) => {
    if (typeof option !== 'object' || option === null) return [];
    const values = option as Record<string, unknown>;
    const raw = stringValue(values['label']);
    if (raw.length === 0) return [];
    const recommended = values['recommended'] === true || /\(recommended\)\s*$/i.test(raw);
    return [
      {
        label: raw.replace(/\s*\(recommended\)\s*$/i, ''),
        description: stringValue(values['description']),
        recommended,
      },
    ];
  });
}

const OWN_ANSWER = '__own__';

/**
 * The question the agent waits on, docked above the composer (design D13, mobile 11): "Question
 * 1 of 2", its choices numbered (the digits pick them), Something else… to type one's own answer
 * in the dock, Skip and Next. Esc puts it aside until the user comes back to it.
 */
export function QuestionDock({ part }: { part: DynamicToolPart }) {
  const resume = useContext(InterruptedActionContext);
  const questions = questionItems(part);
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState<(string | null)[]>([]);
  // The recommended choice is picked already: ↵ takes it.
  const recommended = (position: number) => {
    const found = questionOptions(questions[position] ?? {}).findIndex(
      (option) => option.recommended,
    );
    return found < 0 ? null : String(found);
  };
  const [selected, setSelected] = useState<string | null>(() => recommended(0));
  const [own, setOwn] = useState('');
  const [later, setLater] = useState(false);
  const question = questions[index] ?? {};
  const options = questionOptions(question);
  const total = questions.length;
  const ownInput = useRef<HTMLInputElement>(null);

  const finish = (all: (string | null)[]) => {
    const text =
      total === 1
        ? (all[0] ?? 'Skipped')
        : questions
            .map((item, position) => {
              const title =
                stringValue(item['header']) ||
                stringValue(item['question']) ||
                `Question ${position + 1}`;
              return `${title}: ${all[position] ?? 'skipped'}`;
            })
            .join('\n');
    resume?.onAnswerQuestion(part.toolCallId, text);
  };
  const advance = (answer: string | null) => {
    const all = [...answers];
    all[index] = answer;
    setAnswers(all);
    setOwn('');
    if (index + 1 < total) {
      setSelected(recommended(index + 1));
      setIndex(index + 1);
    } else finish(all);
  };
  const choose = (value: string) => {
    setSelected(value);
    if (value === OWN_ANSWER) requestAnimationFrame(() => ownInput.current?.focus());
  };
  const pick = (choice: number) => {
    if (choice === options.length) choose(OWN_ANSWER);
    else if (options[choice] !== undefined) choose(String(choice));
  };
  const answer =
    selected === OWN_ANSWER
      ? own.trim() === ''
        ? null
        : own.trim()
      : selected === null
        ? null
        : (options[Number(selected)]?.label ?? null);
  const next = () => {
    if (answer !== null) advance(answer);
  };
  const keys = useDockKeys(later ? {} : { deny: () => setLater(true), pick, next });

  if (later) {
    return (
      <div className="mx-auto mb-3 flex max-w-conversation items-center gap-2 rounded-lg border border-attention bg-card px-4 py-2">
        <MessageCircleQuestion aria-hidden className="size-4 text-attention" />
        <span className="type-meta flex-1 truncate text-foreground">
          {stringValue(question['question']) || 'The agent asks you something'}
        </span>
        <Button
          className="h-8 rounded-control px-3.5"
          onClick={() => setLater(false)}
          size="sm"
          variant="outline"
        >
          Answer
        </Button>
      </div>
    );
  }

  // The number keys show only while they pick (focus in the dock, or nowhere).
  const choiceKey = (position: number) =>
    keys.reach === 'all' && (
      <span className="flex size-5 shrink-0 items-center justify-center rounded-md border border-hairline text-caption text-muted-foreground max-md:hidden">
        {position + 1}
      </span>
    );
  return (
    <DockFrame
      data-dock="question"
      keys={keys}
      icon={<MessageCircleQuestion aria-hidden className="size-3.5" />}
      label={total > 1 ? `Question ${index + 1} of ${total}` : 'Question'}
    >
      <p
        className="type-body mt-2 font-semibold text-foreground"
        id={`${part.toolCallId}-question`}
      >
        {stringValue(question['question']) || stringValue(question['prompt'])}
      </p>
      <RadioGroup
        aria-labelledby={`${part.toolCallId}-question`}
        className="mt-3 gap-1.5"
        onValueChange={choose}
        value={selected ?? ''}
      >
        {options.map((option, position) => (
          <RadioGroupItem
            className="items-center py-2"
            indicator={false}
            key={option.label}
            value={String(position)}
          >
            {choiceKey(position)}
            <span className="min-w-0 flex-1">
              <span className="flex items-center gap-2 text-foreground text-sm">
                {option.label}
                {option.recommended && (
                  <span className="rounded-sm bg-control px-1.5 font-medium text-caption text-muted-foreground leading-4">
                    Recommended
                  </span>
                )}
              </span>
              {option.description.length > 0 && (
                <span className="type-meta block text-muted-foreground">{option.description}</span>
              )}
            </span>
          </RadioGroupItem>
        ))}
        <RadioGroupItem className="items-center py-2.5" indicator={false} value={OWN_ANSWER}>
          {choiceKey(options.length)}
          <span className="text-foreground text-sm">Something else…</span>
        </RadioGroupItem>
      </RadioGroup>
      {selected === OWN_ANSWER && (
        <Input
          aria-label="Your own answer"
          className="mt-2 h-control-lg rounded-xl bg-control"
          onChange={(event) => setOwn(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              next();
            }
          }}
          placeholder="Or type your own answer…"
          ref={ownInput}
          value={own}
        />
      )}
      <div className="mt-3 flex items-center gap-2">
        <p className="type-meta flex-1 text-muted-foreground max-md:hidden">
          {keys.reach === 'all'
            ? `1–${options.length + 1} to pick · ↵ next · Esc to answer later`
            : keys.reach === 'composer'
              ? 'Esc to answer later'
              : null}
        </p>
        <Button
          className="h-9 rounded-control px-4 max-md:h-12"
          onClick={() => advance(null)}
          variant="outline"
        >
          Skip
        </Button>
        <Button
          className="h-9 rounded-control px-4 max-md:h-12 max-md:flex-1 max-md:text-base"
          disabled={answer === null}
          onClick={next}
        >
          {index + 1 < total ? 'Next' : 'Send'}
          <Key>↵</Key>
        </Button>
      </div>
    </DockFrame>
  );
}

/**
 * What the agent waits on, docked above the composer with an amber border (design D13, D14,
 * D48b): an approval (a command to run, an app to use, the handoff it proposes), else the question
 * it asks. Approving a command opens the Terminal beside the conversation; approving the handoff
 * opens Continue on this computer.
 */
export function ToolApprovalPanel({
  messages,
  onAnswer,
  preview = false,
}: {
  messages: readonly CrowdexUIMessage[];
  onAnswer: AnswerToolApproval;
  preview?: boolean;
}) {
  const approvals = pendingToolApprovals(messages);
  const question = approvals.length === 0 ? pendingQuestion(messages) : null;
  const store = useOptionalDesktopShellStoreApi();
  const workbench = useWorkbench();
  const resume = useContext(InterruptedActionContext);
  const first = approvals[0];

  if (first !== undefined)
    return (
      <ApprovalDock
        approval={first}
        count={approvals.length}
        key={first.approvalId}
        onAnswer={(answer, kind) => {
          if (!preview) void onAnswer(answer);
          if (!answer.approved || store === null) return;
          const state = store.getState();
          if (kind === 'handoff') state.setLocalHandoffOpen(true);
          if (kind === 'command') {
            // What runs shows beside the conversation (design D14).
            const terminal = workbench.panels.find(
              (panel) => panel.session?.surface === 'terminal',
            )?.view;
            if (terminal !== undefined) state.setPanelTabs((tabs) => showPanelView(tabs, terminal));
            state.setSidePanelOpen(true);
          }
        }}
      />
    );
  if (question !== null && resume !== null)
    return <QuestionDock key={question.toolCallId} part={question} />;
  return null;
}
